import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { OctagonAlertIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { HaltButton } from "@/components/HaltButton";
import { HALT_CHANGED, announceHaltChange, fetchCampaignGroupOverview, request, releaseSending } from "@/lib/api";
import type { CampaignGroupState } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { activityLine } from "@/components/emc/status";
import { timeAtViewer } from "@/components/emc/time";

/** F1 — the red bar that says the platform is stopped, and the way out of it.
 *
 * It is in the shell rather than on a page because a halt is not a fact about
 * one screen: whoever is looking at anything has to see it. It polls, because
 * the halt may be thrown from a terminal by somebody who is not at this
 * keyboard, and a screen that says "sending" about a stopped platform is worse
 * than no screen. A page that throws the halt itself (the warmup page's
 * "Teljes leállítás") announces it, and the bar reads again at once.
 *
 * Nothing is shown here while the platform is sending — the ordinary state is
 * one quiet line in the sidebar's footer (`SendingState`, UI-8) — and the
 * release button is only offered to an admin, because that is who the API lets
 * through. The release asks why in a dialog (UI-6), like every other move that
 * names a person.
 *
 * The shell reads the status once (`HaltStatusProvider`) for both: the bar and
 * the footer can never disagree.
 */

interface HaltStatus {
  halted: boolean;
  halted_at: string | null;
  halted_by: string | null;
  note: string | null;
  takes_effect_within_seconds: number | null;
}

const POLL_MS = 15000;

interface HaltSlot {
  status: HaltStatus | null;
  /** When the status on screen was read. */
  at: Date | null;
  setStatus: (status: HaltStatus) => void;
}

const HaltContext = createContext<HaltSlot | null>(null);

export function HaltStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatusState] = useState<HaltStatus | null>(null);
  const [at, setAt] = useState<Date | null>(null);
  const setStatus = useCallback((next: HaltStatus) => {
    setStatusState(next);
    setAt(new Date());
  }, []);

  const read = useCallback(async () => {
    try {
      setStatus(await request<HaltStatus>("/sending/status"));
    } catch {
      // A status that cannot be read is not a halt. The bar stays as it was:
      // guessing either way would be a lie on every page.
    }
  }, [setStatus]);

  useEffect(() => {
    void read();
    const timer = window.setInterval(() => void read(), POLL_MS);
    const now = () => void read();
    window.addEventListener(HALT_CHANGED, now);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(HALT_CHANGED, now);
    };
  }, [read]);

  return <HaltContext.Provider value={{ status, at, setStatus }}>{children}</HaltContext.Provider>;
}

// A campaign that is going out, or waiting on its A/B test to decide — the
// overview page's own reading of "running".
const RUNNING: CampaignGroupState[] = ["sending", "ab_testing", "ab_deciding"];
const ACTIVITY_POLL_MS = 60000;
// The overview endpoint's own ceiling on `limit`.
const ACTIVITY_ROWS = 200;

/** What is going out right now, in a few words: "enabled" alone said nothing
 * about whether anything was being sent (owner, 2026-10-06). The counts cover
 * every group, not one page; a failed read keeps the last line. */
function useActivityLine(): string | null {
  const [line, setLine] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    const read = async () => {
      try {
        // The rows too, not only the counts: a group whose countries are all
        // paused is still `sending`, and is not "running" on this line.
        const answer = await fetchCampaignGroupOverview({ states: RUNNING, limit: ACTIVITY_ROWS });
        if (live) setLine(activityLine(answer.counts, answer.items));
      } catch {
        // Not knowing is not "nothing runs": the line stays as it was.
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), ACTIVITY_POLL_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, []);
  return line;
}

/** The sidebar footer's line: sending is allowed or stopped, what runs, and
 * the stop itself, for an admin. Until the first answer: nothing claimed. */
export function SendingState() {
  const slot = useContext(HaltContext);
  const status = slot?.status ?? null;
  const halted = status?.halted ?? false;
  const activity = useActivityLine();
  const time = slot?.at ? timeAtViewer(slot.at) : null;
  return (
    <div className="flex items-center gap-2.5 rounded-inner bg-fill-secondary py-2 pr-2 pl-3" role="status">
      <span
        aria-hidden="true"
        className={
          status === null
            ? "size-2 shrink-0 rounded-full bg-tertiary"
            : halted
              ? "size-2 shrink-0 rounded-full bg-destructive outline-3 outline-red-soft"
              : "size-2 shrink-0 rounded-full bg-success outline-3 outline-green-soft"
        }
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className={halted ? "truncate text-detail font-semibold text-red-ink" : "truncate text-detail font-semibold"}>
          {status === null ? "A kiküldés állapota…" : halted ? "A kiküldés leállítva" : "Kiküldés engedélyezve"}
        </span>
        <span
          className="truncate text-label text-muted-foreground"
          title={time ? `ellenőrizve ${time}` : undefined}
        >
          {halted ? "feloldás a piros sávon" : (activity ?? (time ? `ellenőrizve ${time}` : "még nincs válasz"))}
        </span>
      </div>
      {status !== null && <HaltButton halted={halted} compact />}
    </div>
  );
}

export default function SendingHaltBar() {
  const { user } = useAuth();
  const slot = useContext(HaltContext);
  const status = slot?.status ?? null;
  const [open, setOpen] = useState(false);

  if (slot === null || status === null || !status.halted) return null;
  const setStatus = slot.setStatus;

  const seconds = status.takes_effect_within_seconds ?? 60;
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-destructive/30 bg-red-soft px-4 py-2 text-red-ink md:px-8"
      role="status"
    >
      <OctagonAlertIcon className="size-4 shrink-0" aria-hidden="true" />
      <strong className="font-semibold">A kiküldés áll.</strong>
      <span className="min-w-0 text-foreground">
        {status.halted_by} állította le
        {status.note ? `: ${status.note}` : "."} A workerek legfeljebb {seconds} másodperc
        alatt veszik észre.
      </span>
      {user?.role === "admin" && (
        <>
          <Button
            className="ml-auto bg-card"
            variant="outline-danger"
            size="sm"
            type="button"
            onClick={() => setOpen(true)}
          >
            Feloldás
          </Button>
          <ActionDialog
            open={open}
            onOpenChange={setOpen}
            title="A kiküldés újraindítása"
            description={
              <>
                Minden pool és kampány onnan folytatja, ahol a leállításkor tartott; a workerek
                legfeljebb {seconds} másodperc alatt veszik észre. Egy megállított pool megállítva
                marad: azt a warmup-oldalon kell külön feloldani.
              </>
            }
            confirmLabel="Kiküldés újraindítása"
            reason={{ label: "Miért indulhat újra a kiküldés?", max: 300 }}
            onConfirm={async (note) => {
              setStatus(await releaseSending(note));
              announceHaltChange();
              toast("A kiküldés újraindult.");
            }}
          />
        </>
      )}
    </div>
  );
}
